import clsx, { type ClassValue } from 'clsx'
import React, { ElementType, ReactElement } from 'react'
import { extendTailwindMerge } from 'tailwind-merge'
import { defaultConfig, tv } from 'tailwind-variants'

defaultConfig.twMerge = false

const isArbitraryNumber = (value: string) =>
  /^\[[\d.]+\]$/.test(value) || !Number.isNaN(Number(value))

const twMerge = extendTailwindMerge<
  'capsize' | 'capsize-before' | 'capsize-after'
>({
  extend: {
    classGroups: {
      capsize: [{ capsize: ['none', isArbitraryNumber] }],
      'capsize-before': [{ 'capsize-before': [isArbitraryNumber] }],
      'capsize-after': [{ 'capsize-after': [isArbitraryNumber] }]
    },
    conflictingClassGroups: {
      capsize: ['capsize-before', 'capsize-after'],
      'capsize-before': ['capsize'],
      'capsize-after': ['capsize']
    }
  }
})

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs))

type Breakpoint = '@initial' | '@sm' | '@md' | '@lg' | '@xl'

export const Theme = ({
  children,
  theme
}: React.PropsWithChildren<{ theme?: Record<string, string> }>) => (
  <div className="contents" style={theme}>
    {children}
  </div>
)

export const createTheme = (config: Record<string, Record<string, string>>) =>
  Object.fromEntries(
    Object.values(config).flatMap((tokens) =>
      Object.entries(tokens).map(([token, value]) => [`--${token}`, value])
    )
  )

// ---------------------------------------------------------------------------
// Local typing for styled()
// ---------------------------------------------------------------------------

// Responsive variant prop: either a single value or a per-breakpoint map
// Each breakpoint can have a different value from the union type V
type ResponsiveVariant<V extends string | number | boolean> =
  | V
  | Partial<Record<Breakpoint, V>>

// Helpers to derive value type from variant config keys
type Keys<V> = keyof V
type StringKeys<V> = Extract<Keys<V>, string>
type NumberKeys<V> = Extract<Keys<V>, number>
// Booleans are represented as string keys "true"/"false" in our VariantConfig,
// so we only need to detect those in StringKeys.
type BooleanKeys<V> = Extract<StringKeys<V>, 'true' | 'false'>

type HasBoolLikeKeys<V> =
  BooleanKeys<V> extends never
    ? Extract<StringKeys<V>, 'true' | 'false'> extends never
      ? false
      : true
    : true

// If a variant's keys are boolean-like, expose the prop as boolean.
// If it has numeric keys, allow those numbers and their string forms.
// Otherwise use a string union of the keys.
type VariantValueType<V> =
  HasBoolLikeKeys<V> extends true
    ? boolean
    : [NumberKeys<V>] extends [never]
      ? StringKeys<V>
      : NumberKeys<V> | `${NumberKeys<V>}`

// Shape of the `variants` section passed to styled()
type VariantConfig = Record<string, Record<string | number, string | string[]>>

// Options for styled() function
type StyledOptions = {
  enabledResponsiveVariants?: boolean
}

// Helper to check if responsive variants are enabled
type AreResponsiveVariantsEnabled<O> = O extends {
  enabledResponsiveVariants: true
}
  ? true
  : false

// Props derived from a `variants` config
// If enabledResponsiveVariants is true, all variants support responsive values
// If enabledResponsiveVariants is not provided or false, variants do not support responsive values
type VariantProps<
  V extends VariantConfig | undefined,
  R extends boolean = false
> = V extends VariantConfig
  ? R extends true
    ? {
        [K in keyof V]?: ResponsiveVariant<VariantValueType<V[K]>>
      }
    : {
        [K in keyof V]?: VariantValueType<V[K]>
      }
  : unknown

// Typed config for our styled helper.
// We intentionally *don't* rely on tailwind-variants' TS types here;
// we only care about enough structure to derive props.
type StyledConfig<V extends VariantConfig | undefined = undefined> = {
  base?: string | string[]
  variants?: V
  compoundVariants?: Array<
    {
      class?: string | string[]
    } & (V extends VariantConfig
      ? {
          [K in keyof V]?: VariantValueType<V[K]> | VariantValueType<V[K]>[]
        }
      : Record<string, never>)
  >
  defaultVariants?: V extends VariantConfig
    ? { [K in keyof V]?: VariantValueType<V[K]> }
    : Record<string, never>
}

// Function overloads for better type inference
export function styled<TElement extends ElementType, V extends VariantConfig>(
  el: TElement,
  styles: StyledConfig<V>,
  options: { enabledResponsiveVariants: true }
): React.ForwardRefExoticComponent<
  Omit<React.ComponentPropsWithRef<TElement>, keyof V> &
    VariantProps<V, true> & { as?: ElementType }
>

export function styled<
  TElement extends ElementType,
  V extends VariantConfig | undefined = undefined
>(
  el: TElement,
  styles: StyledConfig<V>
): React.ForwardRefExoticComponent<
  Omit<
    React.ComponentPropsWithRef<TElement>,
    V extends VariantConfig ? keyof V : never
  > &
    VariantProps<V, false> & { as?: ElementType }
>

// Implementation
export function styled<
  TElement extends ElementType,
  V extends VariantConfig | undefined = undefined,
  O extends StyledOptions | undefined = undefined
>(el: TElement, styles: StyledConfig<V>, options?: O) {
  const variantKeys = Object.keys(styles.variants || {})
  const enabledResponsiveVariants = options?.enabledResponsiveVariants === true
  const tvFn = tv(styles as any)

  type ResponsiveVariantsEnabled = AreResponsiveVariantsEnabled<O>
  type VariantPropsType = VariantProps<V, ResponsiveVariantsEnabled>
  type BaseProps = React.ComponentPropsWithRef<TElement>
  // Extract variant keys to omit from BaseProps so variant props override native HTML attributes
  type VariantKeys = V extends VariantConfig ? keyof V : never
  type StyledProps = Omit<BaseProps, VariantKeys> &
    VariantPropsType & { as?: ElementType }

  const Comp = React.forwardRef<React.ComponentRef<TElement>, StyledProps>(
    (props, ref) => {
      const additionalStyles = enabledResponsiveVariants
        ? variantKeys
            .map((variantKey) => {
              const responsiveVariantConfig:
                | Record<Breakpoint, string | number>
                | undefined = props[variantKey]
              const variantStyles: Record<string, string | string[]> =
                styles.variants![variantKey]

              if (
                typeof responsiveVariantConfig === 'object' &&
                responsiveVariantConfig &&
                !Array.isArray(responsiveVariantConfig)
              ) {
                return Object.entries(responsiveVariantConfig).flatMap(
                  ([key, val]: [Breakpoint, string | number]) => {
                    const valStr = String(val)
                    const styleArr: string | string[] =
                      variantStyles && variantStyles[valStr]

                    if (styleArr && Array.isArray(styleArr)) {
                      return styleArr.flatMap((cls: string) => {
                        if (key === '@initial') return cls
                        return `${key.replace('@', '')}:${cls}`
                      })
                    }
                    return []
                  }
                )
              }

              return []
            })
            .flat()
        : []

      // Filter out variant props that don't belong to this component before calling tvFn
      // This prevents tailwind-variants from processing props meant for wrapped components
      // Only pass props that are in this component's variant keys
      // For boolean variants, only include them if the value is explicitly true or false (not null/undefined)
      // Note: tailwind-variants treats null as "no variant" but undefined/missing as "apply false variant"
      // So we need to convert undefined to null for boolean variants to prevent default behavior
      const propsForTv =
        variantKeys.length > 0
          ? Object.fromEntries(
              Object.entries(props)
                .filter(([key]) => variantKeys.includes(key))
                .map(([key, value]) => {
                  // Check if this is a boolean variant (has both 'true' and 'false' keys)
                  const variantConfig = styles.variants?.[key]
                  if (
                    variantConfig &&
                    'true' in variantConfig &&
                    'false' in variantConfig
                  ) {
                    // For boolean variants, only include if value is explicitly true or false
                    // Convert undefined to null to prevent tailwind-variants from applying false variant
                    if (value === true || value === false) {
                      return [key, value]
                    }
                    // For null/undefined, pass null to prevent tailwind-variants from applying defaults
                    // This ensures that when a boolean prop is explicitly null/undefined, no variant is applied
                    return [key, null]
                  }
                  // For non-boolean variants, pass value as-is (including null/undefined)
                  return [key, value]
                })
            )
          : {}

      const cls = tvFn(propsForTv)

      const filteredProps = Object.fromEntries(
        Object.entries(props).filter(
          ([key]) =>
            !variantKeys.includes(key) &&
            key !== 'as' &&
            key !== '__wrapperClasses'
        )
      )

      const mergedClassName = clsx(cls, additionalStyles).trim()
      const propsClassName = (props as any).className
      // Check for wrapper classes passed from a parent styled component
      const wrapperClasses = (props as any).__wrapperClasses

      if (
        typeof el === 'string' ||
        typeof el === 'function' ||
        (el && typeof el === 'object')
      ) {
        const elementType = el as ElementType

        // If el is a host string ('div', 'span', etc.) we must not pass variant props to the DOM.
        if (typeof el === 'string') {
          const { className: _, ...restFilteredProps } = filteredProps as any
          // Merge order: wrapper classes (lowest priority) < component classes < user's className (highest priority)
          // This ensures variant classes from this component override base classes from wrapper
          // IMPORTANT: When propsClassName exists (could be from asChild), we need to ensure child's classes win
          // over parent classes, but user's className should still have highest priority.
          // Strategy: Only apply special merge when wrapperClasses exist (nested styled component scenario).
          // For regular cases (no wrapperClasses), use standard merge order so user's className has highest priority.
          const finalClassName =
            wrapperClasses && propsClassName
              ? twMerge(wrapperClasses, propsClassName, mergedClassName) // This component's classes win by being merged last
              : wrapperClasses
                ? twMerge(wrapperClasses, mergedClassName, propsClassName) // Standard merge when no propsClassName
                : propsClassName
                  ? (() => {
                      // When propsClassName comes from asChild (Radix), we want this component's classes to win
                      // When propsClassName is a regular className prop, the user's className should win (highest priority)
                      // We can detect asChild scenario by checking if propsClassName contains classes that look like they came from a parent component
                      // (e.g., data attribute variants like data-[state=closed]:rounded-sm)
                      // If it does, treat it as asChild and let component classes win. Otherwise, let user's className win.
                      const hasDataAttributeVariants = propsClassName
                        .split(' ')
                        .some((cls) => /^data-\[state=[^\]]+\]:/.test(cls))

                      if (hasDataAttributeVariants) {
                        // This is likely from asChild - component's classes should win
                        return twMerge(propsClassName, mergedClassName)
                      } else {
                        // Regular className prop - user's className should win (highest priority)
                        return twMerge(mergedClassName, propsClassName)
                      }
                    })()
                  : twMerge(mergedClassName) // Always merge mergedClassName to resolve conflicts within it (e.g., bg-[unset] vs bg-(--base))

          return React.createElement(
            (props as { as?: ElementType }).as || elementType,
            {
              ...restFilteredProps,
              ref,
              className: finalClassName
            }
          )
        }

        // If el is a function component (including forwardRef), pass ALL props through
        // (the wrapped component may expect variant props), but ensure className and ref are forwarded.
        // We need to pass all props (not filteredProps) because the underlying component may have its own variants
        //
        // For className merging with wrapped styled components:
        // Solution: Pass wrapper's classes via __wrapperClasses prop for styled components to merge correctly.
        // For regular function components, merge wrapper classes with our classes and pass as className.
        // This ensures:
        //   - Styled components can merge: wrapper classes < wrapped component classes < user's className
        //   - Regular components get merged classes directly and don't receive __wrapperClasses
        // Filter out variant props and __wrapperClasses from props passed to function components
        // This prevents them from ending up in the DOM.
        // Note: We filter out variant props that belong to THIS component. Variant props that belong to
        // the wrapped component will be passed through (they're not in variantKeys), and the wrapped
        // component will filter them if it's a styled component.
        // Note: 'as' prop is filtered from filteredProps to prevent it from going to DOM for string elements,
        // but we add it back for function components since they need it for polymorphism.
        const { className: _, ...restFilteredProps } = filteredProps as any
        const asProp = (props as any).as // Extract 'as' prop separately
        // Note: __wrapperClasses is already filtered out in filteredProps, so restFilteredProps doesn't include it

        // For styled components: Pass wrapper classes via __wrapperClasses so they can merge correctly.
        // Styled components extract __wrapperClasses from props (before filteredProps) and filter it in filteredProps.
        // For regular function components: We merge wrapper classes into className, but also pass __wrapperClasses
        // which they may spread. To prevent it in DOM, we need to ensure it's filtered, but we can't control that.
        // However, styled components that wrap regular components will filter __wrapperClasses in their filteredProps.
        if (wrapperClasses) {
          // We have wrapper classes from parent - this is a nested styled component scenario
          // Merge order: wrapper classes (base) < this component's classes (variants) < user's className
          // This ensures variant classes override base classes via twMerge
          // IMPORTANT: When propsClassName exists (could be from asChild), we need to ensure this component's classes win.
          // Strategy: Merge wrapperClasses with propsClassName first, then merge with mergedClassName last.
          // This ensures: wrapperClasses < propsClassName < mergedClassName (this component's classes win)
          // This is critical when propsClassName contains parent classes from asChild merge.
          const finalClassName = propsClassName
            ? twMerge(wrapperClasses, propsClassName, mergedClassName) // This component's classes win by being merged last
            : twMerge(wrapperClasses, mergedClassName)

          // Check if the wrapped component is a styled component
          // React.forwardRef components are objects, not functions
          const isStyledComponent =
            (typeof elementType === 'function' ||
              typeof elementType === 'object') &&
            elementType !== null &&
            (elementType as any).__isStyledComponent === true

          const propsForComponent: any = {
            ...restFilteredProps, // Base props (filters THIS component's variant props, __wrapperClasses, 'as')
            ...(asProp ? { as: asProp } : {}), // Add 'as' prop back for function components (polymorphic support)
            // Note: We don't add variant props back - they're already filtered by filteredProps.
            ref
          }

          if (isStyledComponent) {
            // Wrapped component is a styled component - pass __wrapperClasses so variant classes win
            // IMPORTANT: When propsClassName exists (could be from asChild), it may contain both parent and child classes.
            // We should NOT merge propsClassName with mergedClassName here, because that would remove child classes.
            // Instead, pass propsClassName as-is (or merge only wrapperClasses with mergedClassName) so the child
            // can merge its own classes last. The child will merge: twMerge(__wrapperClasses, propsClassName, child's mergedClassName).
            // This ensures child's classes win by being merged last.
            // Merge wrapperClasses with mergedClassName to create the wrapper classes for the child
            const combinedWrapperClasses = twMerge(
              wrapperClasses,
              mergedClassName
            )
            propsForComponent.__wrapperClasses = combinedWrapperClasses
            propsForComponent.className = propsClassName || undefined
          } else {
            // Wrapped component is not a styled component - merge wrapper classes into className
            // and don't pass __wrapperClasses (it would end up in DOM)
            propsForComponent.className = finalClassName
          }
          return React.createElement(elementType, propsForComponent)
        } else {
          // No wrapper classes from parent - we're top-level
          // For function components: Pass __wrapperClasses so styled components can merge correctly.
          // Styled components extract __wrapperClasses from their props (before filteredProps) and filter it in filteredProps.
          // Regular function components will receive __wrapperClasses but should filter it out themselves.
          // We also merge wrapper classes into className for regular function components.
          // Note: We pass __wrapperClasses in props (not filteredProps) so styled components can access it,
          // but styled components filter it in their filteredProps so it won't go to DOM.
          // Regular function components that spread all props will pass __wrapperClasses to DOM, which is a problem.
          // But we can't distinguish them, so we rely on styled components filtering it.
          // Check if the wrapped component is a styled component
          // elementType is the component itself (e.g., Inner when Outer extends Inner)
          // React.forwardRef components are objects, not functions, but we can still check the flag
          const isStyledComponent =
            (typeof elementType === 'function' ||
              typeof elementType === 'object') &&
            elementType !== null &&
            (elementType as any).__isStyledComponent === true

          const propsForComponent: any = {
            ...restFilteredProps, // Filters out __wrapperClasses, variant props, 'as'
            ...(asProp ? { as: asProp } : {}),
            ref
          }

          if (isStyledComponent) {
            // Styled component - pass wrapper classes via __wrapperClasses so variant classes can take precedence
            // IMPORTANT: When propsClassName exists, we need to distinguish between:
            // 1. User's className (should win - highest priority)
            // 2. className from asChild/parent component (component's classes should win)
            // We detect asChild by checking for data attribute variants (e.g., data-[state=closed]:rounded-sm)
            const hasDataAttributeVariants = propsClassName
              ? propsClassName
                  .split(' ')
                  .some((cls) => /^data-\[state=[^\]]+\]:/.test(cls))
              : false

            const mergedPropsAndComponent = propsClassName
              ? hasDataAttributeVariants
                ? // This is likely from asChild - component's classes should win
                  twMerge(propsClassName, mergedClassName)
                : // Regular user className - user's className should win (highest priority)
                  twMerge(mergedClassName, propsClassName)
              : mergedClassName
            propsForComponent.__wrapperClasses = mergedClassName
            propsForComponent.className = mergedPropsAndComponent || undefined
          } else {
            // Regular function component - merge wrapper classes into className
            // Standard merge order: component classes < user's className (highest priority)
            propsForComponent.className = twMerge(
              mergedClassName,
              propsClassName
            )
          }
          return React.createElement(elementType, propsForComponent)
        }
      }

      if (React.isValidElement(el)) {
        const element = el as ReactElement<unknown>
        // Merge existing element className, incoming filteredProps.className and our computed class
        const existingClass = (element.props as any).className
        const incomingClass = (filteredProps as any).className
        const finalClassName = cn(
          existingClass,
          incomingClass,
          mergedClassName
        ).trim()

        const cloneProps: any = {
          // ...element.props,
          ...filteredProps,
          as:
            (props as { as?: ElementType }).as ||
            (element.props as { as?: ElementType }).as,
          className: finalClassName
        }

        // Forward the ref if provided
        if (ref) {
          cloneProps.ref = ref
        }

        return React.cloneElement(element, cloneProps)
      }

      throw Error('Something is wrong')
    }
  )

  // Mark the component as a styled component so we can detect it later
  ;(Comp as any).__isStyledComponent = true

  return Comp
}
